/**
 * Lightweight ring buffer of recent PAX terminal transactions, persisted
 * to localStorage. Used by the PAX Test Panel so the merchant can:
 *   • see the last 100 calls (Ping / Sale / Void / Refund / Tip-Adjust)
 *     without having to take a screenshot of every test
 *   • copy raw_hex / response_code from a failed call to share with
 *     us or with Empire Paytech support
 *
 * Trimmed to MAX entries to avoid unbounded localStorage growth.
 */

const KEY = 'pax_txn_log_v1';
const MAX = 100;
const LISTENERS = new Set();

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const write = (entries) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries.slice(0, MAX)));
  } catch (e) {
    // Quota or disabled storage — swallow; the log is best-effort.
  }
  LISTENERS.forEach((fn) => { try { fn(entries); } catch { /* ignore */ } });
};

/** Append a single PAX call to the log (newest first). */
export const logPaxTransaction = (label, params, result) => {
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ts: new Date().toISOString(),
    label,
    params: params || {},
    success: !!(result && (result.success || result.approved)),
    response_code: result?.responseCode || result?.response_code || '',
    response_message: result?.responseMessage || result?.responseMessage || '',
    auth_code: result?.authCode || '',
    host_ref_num: result?.hostRefNum || '',
    raw_hex: result?.raw_hex || '',
    error: result?.error || '',
  };
  const next = [entry, ...read()].slice(0, MAX);
  write(next);
  return entry;
};

/** Get the current log snapshot. */
export const getPaxTransactionLog = () => read();

/** Wipe the log (used by the test panel's Clear button). */
export const clearPaxTransactionLog = () => write([]);

/**
 * Subscribe to log changes — returns an unsubscribe fn. The callback
 * receives the FULL log array on every change, so React subscribers
 * can just `setState(arr)`.
 */
export const subscribePaxTransactionLog = (fn) => {
  LISTENERS.add(fn);
  return () => LISTENERS.delete(fn);
};
